import React, { useState, useRef, useEffect } from 'react';
import { Button } from 'antd';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Loader, ArrowRight, RefreshCcw } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';

const MarkdownContent = styled.div`
  .markdown-content {
    white-space: pre-wrap;
    line-height: 1;
    color: #374151;
    font-size: 0.875rem;
    
    h1, h2, h3, h4 {
      font-weight: 600;
      margin: 0.75em 0 0.25em;
      color: #111827;
    }
    
    p {
      margin: 0.35em 0;
      &:first-child {
        margin-top: 0;
      }
      &:last-child {
        margin-bottom: 0;
      }
    }
    
    ul, ol {
      margin: 0.25em 0 0.5em;
      padding-left: 1.5em;
    }
    
    li {
      margin: 0.15em 0;
      line-height: 1.5;
      
      &:last-child {
        margin-bottom: 0;
      }
    }
    
    strong {
      font-weight: 600;
      color: #111827;
      display: inline;
    }

    h3 {
      font-size: 1rem;
      font-weight: 600;
      margin: 0.75em 0 0.25em;
      color: #111827;
    }

    p strong {
      display: block;
      font-size: 1rem;
      margin: 0.75em 0 0.25em;
      color: #111827;
    }

    p:first-child strong {
      margin-top: 0;
    }

    p + ul {
      margin-top: 0.15em;
    }

    ul {
      list-style-type: disc;
      margin-bottom: 0.5em;
      
      li {
        margin: 0.15em 0;
        padding-left: 0.25em;
      }
    }
  }
`;

const ChatComponent = React.memo(({ messages, onSendMessage, onClickPage, onClearChat, isStreaming }) => {
  const [currentMessage, setCurrentMessage] = useState('');
  const chatContainerRef = useRef(null);

  const clearMessages = () => {
    onClearChat();
  };

  const handleSend = async () => {
    if (currentMessage.trim() !== '' && !isStreaming) {
      const messageToSend = currentMessage;
      setCurrentMessage('');
      try {
        await onSendMessage(messageToSend);
      } catch (error) {
        console.error('Error sending message:', error);
      }
    }
  };

  // Reset input text whenever the messages array is reset to empty (which happens when switching documents)
  useEffect(() => {
    if (messages.length === 0) {
      setCurrentMessage('');
    }
  }, [messages]);

  return (
    <div className="flex flex-col h-full overflow-auto">
      <div className="flex justify-center p-2 bg-gray-50 border-b">
        <Button
          onClick={clearMessages}
          icon={<RefreshCcw className="w-4 h-4" />}
          type="text"
          className="text-gray-600 hover:text-blue-600"
        >
          Clear Chat
        </Button>
      </div>
      <div
        ref={chatContainerRef}
        className="flex-grow p-4 overflow-y-auto space-y-4"
      >
        <AnimatePresence>
          {messages?.map((message, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-xs md:max-w-md px-4 py-2 rounded-2xl border border-gray-200 shadow-xl ${
                message.type === 'user'
                  ? 'bg-gradient-to-bl from-sky-400 to-blue-800 text-white'
                  : 'bg-white text-gray-800'
              }`}>
                {message.type === 'ai' ? (
                  <MarkdownContent>
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeSanitize]}
                      className="markdown-content text-sm font-medium break-words"
                    >
                      {message.content}
                    </ReactMarkdown>
                  </MarkdownContent>
                ) : (
                  <p className="text-sm font-medium break-words">{message.content}</p>
                )}
                <div className="flex">
                  {message.pages?.length > 0 && message.pages.map((page) => (
                    <button
                      key={page}
                      title={`Scroll to page ${page}`}
                      onClick={() => onClickPage(page)}
                      className="flex items-center justify-center w-6 h-6 mr-2 mt-4 bg-blue-500 rounded-full text-white text-1xl cursor-pointer"
                    >
                      {page}
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}

          {isStreaming && messages.length > 0 && messages[messages.length - 1].type === 'ai' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center text-blue-600 bg-white rounded-full px-4 py-2 shadow-sm w-max"
            >
              <Loader className="w-4 h-4 mr-2 animate-spin" />
              <span className="text-sm font-medium">Analyzing document...</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="p-4 bg-white border-t border-gray-200">
        <div className="flex items-center bg-gray-100 rounded-full overflow-hidden shadow-inner">
          <input
            type="text"
            value={currentMessage}
            onChange={(e) => setCurrentMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !isStreaming && handleSend()}
            placeholder="Ask a question about the document..."
            className="flex-grow px-4 py-2 bg-transparent focus:outline-none text-gray-700 text-sm"
            disabled={isStreaming}
          />
          <button
            onClick={handleSend}
            disabled={isStreaming}
            className={`p-2 transition-all duration-300 ease-in-out ${isStreaming ? 'opacity-50 cursor-not-allowed' : 'hover:bg-indigo-100'}`}
          >
            <ArrowRight className={`w-5 h-5 ${isStreaming ? 'text-gray-400' : 'text-blue-600'}`} />
          </button>
        </div>
      </div>
    </div>
  );
});

export default ChatComponent;